import styled from '@emotion/styled';
import { Button, Empty, Modal } from 'antd';
import { useTranslate } from '@tolgee/react';
import { DeleteOutlined, CloseCircleOutlined } from '@ant-design/icons';
import SafeImage from 'components/SafeImage';
import {
  useSelectedItems,
  useToggleSkuChecked,
  useToggleSpuChecked,
  useCleanAllSpu,
} from './state';
import { useStoreInfo } from './useStoreInfo';
import { useContext, useRef } from 'react';
import { ConfigContext } from './context';
import { useVirtualList } from 'ahooks';

function SelectResult({ skuDisabled }: { skuDisabled?: boolean }) {
  const { t } = useTranslate();
  const items = useSelectedItems();
  const selectedCount = items.length;
  const selectedSkuCount = items.reduce(
    (acc, item) => acc + item.skuInfo.filter((e) => e._checked).length,
    0,
  );
  const { skuSelectDisabled } = useContext(ConfigContext);
  const selectedStr =
    skuSelectDisabled || skuDisabled
      ? `SPU(${selectedCount})`
      : `SPU(${selectedCount}) SKU(${selectedSkuCount})`;
  const cleanAllSpu = useCleanAllSpu();
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const shouldVirtual = items.length > 30;
  const [vList] = useVirtualList(items, {
    containerTarget: shouldVirtual ? containerRef : null,
    wrapperTarget: shouldVirtual ? wrapperRef : null,
    overscan: 4,
    itemHeight: (_index, data) => {
      return 111 + Math.ceil((data?.skuInfo?.length || 0) / 2) * 30;
    },
  });

  const hasItems = items.length > 0;

  return (
    <SelectResultContainer>
      <div className="header">
        <div className="title">{t('voucher.已选x', { x: selectedStr })}</div>
        <Button
          type="link"
          onClick={() => {
            Modal.confirm({
              content: t('knowledge.item.clean.confirm'),
              onOk: () => {
                cleanAllSpu();
              },
            });
          }}
        >
          {t('department.清空')}
        </Button>
      </div>
      <div className="list" ref={containerRef}>
        <div ref={wrapperRef}>
          {hasItems ? (
            shouldVirtual ? (
              vList.map(({ data: item }) => (
                <SelectedItemView item={item} key={item.externalItemId} skuDisabled={skuDisabled} />
              ))
            ) : (
              items.map((item) => {
                return (
                  <SelectedItemView
                    item={item}
                    key={item.externalItemId}
                    skuDisabled={skuDisabled}
                  />
                );
              })
            )
          ) : (
            <Empty className="!mt-20" description={t('暂无数据')} />
          )}
        </div>
      </div>
    </SelectResultContainer>
  );
}

function SelectedItemView(props: { item: ItemSelectV2.Item; skuDisabled?: boolean }) {
  const { item, skuDisabled } = props;
  const { t } = useTranslate();
  const toggleSkuChecked = useToggleSkuChecked('item');
  const toggleSpuChecked = useToggleSpuChecked('item');
  const {
    itemName,
    imageUrl: [imageUrl] = [],
    externalItemId: spuId,
    itemSku,
    skuInfo = [],
  } = item;
  const storeStr = useStoreInfo(item);
  const { skuSelectDisabled } = useContext(ConfigContext);

  return (
    <div className="item">
      <div className="item-header">
        <Image hasSmallPic="both" src={imageUrl} width={60} />
        <div className="basic-info">
          <div className="fir-line">
            <div className="item-title">{itemName}</div>
            <div className="delete-spu" onClick={() => toggleSpuChecked(false, spuId)}>
              <DeleteOutlined />
            </div>
          </div>
          <div className="sec-line">
            <div className="store-name">
              {t('marketing.店铺')}: {storeStr}
            </div>
            <div className="extra-info">
              <div className="spu-id">ID: {spuId}</div>
              <Divider>|</Divider>
              <div className="item-sku" title={itemSku}>
                {t('aide.主商品货号')}:{itemSku || '-'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {!skuSelectDisabled && !skuDisabled && (
        <div className="sku-info">
          {skuInfo.map((sku) => {
            return sku._checked ? (
              <div className="sku" key={sku.skuId}>
                <div className="sku-name">{sku.skuName}</div>
                <div
                  className="delete-sku"
                  onClick={() => toggleSkuChecked(false, spuId, sku.skuId)}
                >
                  <CloseCircleOutlined />
                </div>
              </div>
            ) : null;
          })}
        </div>
      )}
    </div>
  );
}

export const Image = styled(SafeImage)`
  width: 60px;
  height: 60px;
`;

export const SelectResultContainer = styled.div`
  padding: 0 12px 12px 12px;
  height: 100%;
  overflow: scroll;
  display: flex;
  flex-direction: column;
  .header {
    display: flex;
    height: 49px;
    padding: 14px 4px 11px 0;
    position: relative;
    top: 0;
    background: var(--fett-erp-card-bg);
    align-items: center;
    justify-content: space-between;
    padding-left: 10px;
    font-size: 16px;
    color: var(--fett-text-primary);
    flex-shrink: 0;
    &::before {
      content: '';
      display: block;
      position: absolute;
      width: 4px;
      height: 16px;
      top: 50%;
      transform: translateY(-6px);
      left: 0px;
      background: var(--ant-primary-color);
    }
    & > .ant-btn-link {
      padding: 0 10px;
    }
  }
  .list {
    flex-grow: 1;
    overflow: auto;
    width: 100%;
    padding-right: 14px;
    .item {
      padding: 10px 13px;
      width: 100%;
      background: var(--fett-bg-panel);
      border: 1px solid var(--fett-border-strong);
      border-radius: 2px;
      margin-bottom: 12px;
      & > .item-header {
        & > .ant-image {
          flex: 0 0 60px;
        }
        display: flex;
        .basic-info {
          margin-left: 8px;
          flex-grow: 1;
          flex-direction: column;
          align-items: center;
          max-width: calc(100% - 68px);
          & > .fir-line {
            width: 100%;
            margin-bottom: 4px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            & > .item-title {
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              color: var(--fett-text-primary);
              font-size: 14px;
            }
            & > .delete-spu {
              flex: 0 0 20px;
              margin-left: 8px;
              width: 20px;
              height: 20px;
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              &:hover {
                color: var(--ant-primary-color);
              }
            }
          }
          & > .sec-line {
            color: var(--fett-text-tertiary);
            font-size: 12px;
            line-height: 18px;
            & > .extra-info {
              display: flex;
              align-items: center;
              .spu-id {
                flex: 0 0 110px;
              }
              .item-sku {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              }
            }
          }
        }
      }
      .sku-info {
        display: flex;
        flex-wrap: wrap;
        &:not(:empty) {
          border-top: 1px solid var(--fett-border-default);
          padding-top: 8px;
          margin-top: 6px;
        }
        & > .sku {
          flex-grow: 0;
          flex-shrink: 0;
          width: calc(50% - 7px);
          display: flex;
          margin-bottom: 8px;
          justify-content: flex-start;
          align-items: center;
          color: rgb(38, 38, 38);
          font-weight: 300;
          &:nth-child(odd) {
            margin-right: 12px;
          }
          .sku-name {
            color: var(--fett-text-secondary);
            font-size: 14px;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
          }
          .delete-sku {
            width: 22px;
            height: 22px;
            flex: 0 0 22px;
            cursor: pointer;
            margin-left: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            &:hover {
              color: var(--ant-primary-color);
            }
          }
        }
      }
    }
  }
`;

export const Divider = styled.div`
  flex: 0 0 4.5%;
  text-align: center;
`;

export default SelectResult;
